import type { ReactElement } from 'react';

import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';

import { kindKey, useDatasetNames } from '../api/catalog';

/* The dataset kind icon for a screen that holds a reference to a dataset rather than its record —
 * an anomaly finding, an incident, an execution task, a compliance row, a reconciliation side.
 *
 * Reads the kind from `useDatasetNames`, the walk those screens already make to turn an id into a
 * name, so it adds no request: React Query serves every icon on the page from one cache entry. A
 * dataset the walk did not reach — or a lookup still loading or failed — renders as unknown rather
 * than as a table, for `DatasetObjectTypeIcon`'s reason.
 *
 * Two ways to name the dataset, because the screens differ: most carry its id; a reconciliation side
 * carries only its connection and qualified name.
 */

export type DatasetKindIconProps =
  | { readonly datasetId: string }
  | { readonly connectionId: string; readonly qualifiedName: string };

export function DatasetKindIcon(props: DatasetKindIconProps): ReactElement {
  const names = useDatasetNames();
  const kind =
    'datasetId' in props
      ? names.data?.kindById.get(props.datasetId)
      : names.data?.kindByName.get(kindKey(props.connectionId, props.qualifiedName));
  return <DatasetObjectTypeIcon objectType={kind} />;
}
